
import React, { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Bot, Send, X, Image as ImageIcon } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

interface ChatMessage {
  id: string;
  company_id: string;
  user_id: string | null;
  user_name: string;
  email: string | null;
  content: string;
  is_admin: boolean;
  created_at: string;
  image_url?: string;
}

interface ChatTabProps {
  visitorName: string;
  visitorEmail: string;
  nameSubmitted: boolean;
  setVisitorName: (name: string) => void;
  setVisitorEmail: (email: string) => void;
  setNameSubmitted: (submitted: boolean) => void;
  messages: ChatMessage[];
  isLoading: boolean;
  isProcessingAI: boolean;
  message: string;
  setMessage: (message: string) => void;
  selectedImage: File | null;
  setSelectedImage: (image: File | null) => void;
  isUploading: boolean;
  sendMessage: (e?: React.FormEvent) => Promise<void>;
  handleSubmitName: (e: React.FormEvent) => void;
  handleImageSelect: () => void;
  removeSelectedImage: () => void;
  formatTime: (dateString: string) => string;
  fileInputRef: React.RefObject<HTMLInputElement>;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  companyName: string;
  aiSupportEnabled: boolean;
  primaryColor?: string;
  textColor?: string;
}

const ChatTab = ({
  visitorName,
  visitorEmail,
  nameSubmitted,
  setVisitorName,
  setVisitorEmail,
  setNameSubmitted,
  messages,
  isLoading,
  isProcessingAI,
  message,
  setMessage,
  selectedImage,
  setSelectedImage,
  isUploading,
  sendMessage,
  handleSubmitName,
  handleImageSelect,
  removeSelectedImage,
  formatTime,
  fileInputRef,
  messagesEndRef,
  companyName,
  aiSupportEnabled,
  primaryColor,
  textColor
}: ChatTabProps) => {
  if (!nameSubmitted) {
    return (
      <div className="flex-1 p-6 flex items-center justify-center">
        <form onSubmit={handleSubmitName} className="w-full max-w-sm space-y-5">
          <div className="space-y-4">
            <h3 className="text-center font-semibold text-lg">
              Enter your details to start chatting
            </h3>
            <div className="space-y-3">
              <Input
                value={visitorName}
                onChange={e => setVisitorName(e.target.value)}
                placeholder="Your name"
                className="w-full h-11 px-4 text-base"
                autoFocus
              />
              <Input
                type="email"
                value={visitorEmail}
                onChange={e => setVisitorEmail(e.target.value)}
                placeholder="Your email"
                className="w-full h-11 px-4 text-base"
              />
            </div>
          </div>
          <Button
            type="submit"
            className="w-full h-11 text-base font-medium"
            style={primaryColor ? {
              backgroundColor: primaryColor,
              color: textColor
            } : {}}
          >
            Start Chat
          </Button>
        </form>
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 p-4 overflow-hidden flex flex-col min-h-0">
        <ScrollArea className="flex-1 pr-2">
          {isLoading ? (
            <div className="flex justify-center items-center h-full min-h-[300px]">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex justify-center items-center h-full min-h-[300px] text-center text-muted-foreground px-4">
              <div className="space-y-2">
                <p className="text-base font-medium">Start a conversation</p>
                <p className="text-sm">Send us a message and we'll respond as soon as possible</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {messages.map(msg => (
                <div key={msg.id} className={`flex ${msg.is_admin ? "justify-start" : "justify-end"} px-1`}>
                  <div className={`flex gap-2.5 max-w-[85%] ${msg.is_admin ? "flex-row" : "flex-row-reverse"}`}>
                    <Avatar className="h-8 w-8 mt-0.5 flex-shrink-0">
                      {msg.is_admin && aiSupportEnabled ? (
                        <div className="bg-primary flex h-full w-full items-center justify-center rounded-full">
                          <Bot className="h-4 w-4 text-primary-foreground" />
                        </div>
                      ) : (
                        <AvatarFallback className="text-xs font-medium">
                          {msg.is_admin ? companyName.substring(0, 2).toUpperCase() : visitorName.substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className={`rounded-2xl px-4 py-2.5 ${msg.is_admin ? "bg-muted text-foreground" : primaryColor ? "text-primary-foreground" : "bg-primary text-primary-foreground"}`}
                        style={!msg.is_admin && primaryColor ? { backgroundColor: primaryColor, color: textColor } : {}}>
                        {msg.image_url && (
                          <div className="mb-2">
                            <img src={msg.image_url} alt="Shared image" className="rounded-lg max-w-full max-h-[200px] object-contain" />
                          </div>
                        )}
                        {msg.content && <p className="text-sm leading-relaxed break-words">{msg.content}</p>}
                      </div>
                      <p className={`text-xs text-muted-foreground mt-1.5 ${msg.is_admin ? "text-left" : "text-right"}`}>
                        {formatTime(msg.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              {isProcessingAI && (
                <div className="flex justify-start px-1">
                  <div className="flex gap-2.5 max-w-[85%]">
                    <Avatar className="h-8 w-8 mt-0.5 flex-shrink-0">
                      <div className="bg-primary flex h-full w-full items-center justify-center rounded-full">
                        <Bot className="h-4 w-4 text-primary-foreground" />
                      </div>
                    </Avatar>
                    <div>
                      <div className="rounded-2xl px-4 py-2.5 bg-muted text-foreground">
                        <div className="flex items-center space-x-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <p className="text-sm">Thinking...</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>
      </div>

      <div className="p-4 border-t flex-shrink-0 bg-background">
        <div className="flex flex-col w-full space-y-3">
          {selectedImage && (
            <div className="w-full bg-muted/50 rounded-xl p-3 border">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                  <img src={URL.createObjectURL(selectedImage)} alt="Selected image" className="h-full w-full object-cover" />
                </div>
                <div className="flex-1 truncate text-sm">{selectedImage.name}</div>
                <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0 hover:bg-destructive/10" onClick={removeSelectedImage} disabled={isUploading}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          <div className="flex w-full items-end gap-2">
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  // Validate file type
                  if (!file.type.startsWith('image/')) {
                    return;
                  }

                  // Validate file size (max 5MB)
                  if (file.size > 5 * 1024 * 1024) {
                    return;
                  }
                  setSelectedImage(file);
                }
              }}
              disabled={isUploading || isProcessingAI}
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="flex-shrink-0 h-10 w-10 hover:bg-muted"
              onClick={handleImageSelect}
              disabled={isUploading || isProcessingAI}
            >
              <ImageIcon className="h-5 w-5" />
            </Button>
            <form onSubmit={sendMessage} className="flex items-center flex-1 gap-2">
              <Input
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder={selectedImage ? "Add a message (optional)" : "Type your message..."}
                className="flex-1 h-10 px-4 rounded-xl text-base"
                disabled={isUploading || isProcessingAI}
              />
              <Button
                type="submit"
                size="icon"
                className="flex-shrink-0 h-10 w-10 rounded-xl"
                disabled={!message.trim() && !selectedImage || isUploading || isProcessingAI}
                style={primaryColor ? {
                  backgroundColor: primaryColor,
                  color: textColor
                } : {}}
              >
                {isUploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatTab;
